import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eye, Filter, ChevronLeft, ChevronRight, ExternalLink, Clock, Shield, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import { useToastContext } from '../contexts/ToastContext'

/**
 * ViewPage Component
 * 
 * This component provides a comprehensive view of threat indicators across all collections
 * in the PickupSTIX platform. It allows users to:
 * - Browse all indicators from all collections in a unified table view
 * - Filter indicators by specific collection using a dropdown
 * - Navigate through large datasets using pagination (10 items per page)
 * - View detailed information including patterns, types, kill chain phases, and metadata
 * - Access individual indicator details via external links
 * 
 * The component fetches data from multiple collections and aggregates indicators
 * for a centralized viewing experience, making it easier for analysts to review
 * threat intelligence data across the entire platform.
 */

/**
 * Interface defining the structure of an indicator object with collection metadata
 * This extends the STIX 2.1 indicator specification with additional collection information
 */
interface IndicatorWithCollection {
  id: string                    // STIX identifier (e.g., "indicator--123e4567-...")
  type: string                  // Always "indicator" for indicator objects
  name?: string                 // Human-readable name for the indicator
  description?: string          // Detailed description of the threat
  pattern?: string              // STIX pattern (e.g., "[file:hashes.MD5 = 'd41d8cd98f00b204e9800998ecf8427e']")
  labels?: string[]            // Legacy STIX 2.0 labels (maintained for compatibility)
  created: string              // ISO 8601 timestamp of creation
  modified: string             // ISO 8601 timestamp of last modification
  valid_from?: string          // When the indicator becomes valid
  valid_until?: string         // When the indicator expires
  indicator_types?: string[]   // STIX 2.1 indicator types (e.g., ["malicious-activity"])
  kill_chain_phases?: Array<{  // Associated kill chain phases
    kill_chain_name: string    // Kill chain framework (e.g., "mitre-attack")
    phase_name: string         // Specific phase (e.g., "command-and-control")
  }>
  collection_id: string        // ID of the collection containing this indicator
  collection_title?: string   // Human-readable collection name
}

// Pagination configuration - number of indicators displayed per page
const ITEMS_PER_PAGE = 10

export default function ViewPage() {
  // UI state management
  const [selectedCollection, setSelectedCollection] = useState<string>('all') // Filter by collection ID or 'all'
  const [currentPage, setCurrentPage] = useState(1)                           // Current pagination page
  const [allIndicators, setAllIndicators] = useState<IndicatorWithCollection[]>([]) // Aggregated indicators from all collections
  const [isLoadingIndicators, setIsLoadingIndicators] = useState(false)      // Loading state for indicator fetching
  const { showError } = useToastContext()

  /**
   * Fetch all available collections from the TAXII server
   * This is used both for filtering and for iterating through collections to get indicators
   */
  const { data: collections, isLoading: collectionsLoading } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.getCollections()
  })

  /**
   * Fetch indicators from all collections and aggregate them
   * 
   * This function:
   * 1. Iterates through all available collections
   * 2. Fetches objects from each collection via TAXII API
   * 3. Filters for indicator objects only (ignoring identities, malware, etc.)
   * 4. Adds collection metadata to each indicator
   * 5. Sorts indicators by modification date (newest first)
   * 6. Handles errors gracefully by continuing with other collections
   */
  const fetchAllIndicators = async () => {
    if (!collections?.collections) return

    setIsLoadingIndicators(true)
    try {
      const allIndicators: IndicatorWithCollection[] = []

      // Iterate through each collection to gather indicators
      for (const collection of collections.collections) {
        try {
          // Fetch all objects from this collection
          const response = await api.getObjects(collection.id)
          
          // Filter for indicator objects only and enrich with collection metadata
          const indicators = response.objects
            .filter((obj: any) => obj.type === 'indicator')  // Only keep STIX indicator objects
            .map((obj: any) => ({
              ...obj,
              collection_id: collection.id,      // Add collection ID for filtering
              collection_title: collection.title // Add human-readable collection name
            }))

          allIndicators.push(...indicators)
        } catch (error) {
          // Log warning but continue processing other collections
          console.warn(`Failed to fetch objects from collection ${collection.id}:`, error)
        }
      }

      // Sort by modification date (newest indicators first)
      allIndicators.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
      
      setAllIndicators(allIndicators)
    } catch (error) {
      console.error('Error fetching indicators:', error)
      showError('Error', 'Failed to load indicators')
    } finally {
      setIsLoadingIndicators(false)
    }
  }

  // Trigger indicator fetching when collections data becomes available
  useEffect(() => {
    fetchAllIndicators()
  }, [collections])

  /**
   * Filter indicators based on selected collection
   * - 'all': Show indicators from all collections
   * - Specific collection ID: Show only indicators from that collection
   */
  const filteredIndicators = selectedCollection === 'all' 
    ? allIndicators
    : allIndicators.filter(ind => ind.collection_id === selectedCollection)

  /**
   * Pagination calculations
   * - Split filtered results into pages of ITEMS_PER_PAGE size
   * - Calculate current page slice for display
   */
  const totalItems = filteredIndicators.length
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const currentIndicators = filteredIndicators.slice(startIndex, endIndex)

  // Reset to first page when changing collection filter to avoid showing empty pages
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCollection])

  /**
   * Format ISO 8601 timestamp to human-readable local date/time
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  /**
   * Get appropriate icon for indicator type
   * - malicious-activity: Warning triangle (red)
   * - attribution: Shield (blue) 
   * - default: Eye (gray)
   */
  const getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'malicious-activity':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'attribution':
        return <Shield className="h-4 w-4 text-blue-500" />
      default:
        return <Eye className="h-4 w-4 text-gray-500" />
    }
  }

  /**
   * Get CSS classes for indicator type badges based on threat level
   * Colors follow common threat intelligence conventions:
   * - Red: Malicious activity (high threat)
   * - Blue: Attribution/analysis (medium threat)
   * - Gray: Unknown/other (neutral)
   */
  const getTypeColor = (types: string[] = []) => {
    if (types.includes('malicious-activity')) return 'bg-red-100 text-red-800'
    if (types.includes('attribution')) return 'bg-blue-100 text-blue-800' 
    return 'bg-gray-100 text-gray-800'
  }

  if (collectionsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Eye className="h-6 w-6" />
              <span>View Indicators</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Browse threat indicators across all collections
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Collections</option>
                {collections?.collections?.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Eye className="h-5 w-5 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Indicators</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {isLoadingIndicators ? '...' : allIndicators.length.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Filter className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Filtered Results</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {filteredIndicators.length.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Collections</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {collections?.collections?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoadingIndicators ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading indicators...</p>
        </div>
      ) : (
        <>
          {/* Indicators Table */}
          {currentIndicators.length > 0 ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Indicator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Collection
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kill Chain
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Modified
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentIndicators.map((indicator) => (
                      <tr key={indicator.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-3">
                            {getTypeIcon(indicator.indicator_types?.[0] || '')}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {indicator.name || indicator.id}
                              </div>
                              {indicator.description && (
                                <div className="text-sm text-gray-500 max-w-xs truncate">
                                  {indicator.description}
                                </div>
                              )}
                              {indicator.pattern && (
                                <div className="text-xs text-blue-600 font-mono bg-blue-50 px-2 py-1 rounded mt-1 max-w-xs truncate">
                                  {indicator.pattern}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {indicator.indicator_types?.map((type, idx) => (
                              <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor([type])}`}>
                                {type}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {indicator.collection_title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {indicator.collection_id}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {indicator.kill_chain_phases?.map((phase, idx) => (
                              <div key={idx} className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {phase.phase_name.replace(/-/g, ' ')}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div>{formatDate(indicator.modified)}</div>
                          {indicator.valid_until && (
                            <div className="text-xs text-orange-600">
                              Expires: {formatDate(indicator.valid_until)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => window.open(`/objects/${indicator.id}`, '_blank')}
                            className="text-primary-600 hover:text-primary-900 text-sm font-medium flex items-center space-x-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
                        <span className="font-medium">{totalItems}</span> results
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>

                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum
                          if (totalPages <= 5) {
                            pageNum = i + 1
                          } else if (currentPage <= 3) {
                            pageNum = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i
                          } else {
                            pageNum = currentPage - 2 + i
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === currentPage
                                  ? 'border-primary-500 bg-primary-50 text-primary-600'
                                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No indicators found</h3>
              <p className="text-gray-500 mb-6">
                {selectedCollection === 'all' 
                  ? "There are no indicators available in any collection."
                  : "No indicators found in the selected collection."
                }
              </p>
              {selectedCollection !== 'all' && (
                <button
                  onClick={() => setSelectedCollection('all')}
                  className="text-primary-600 hover:text-primary-500 font-medium"
                >
                  View all collections
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}